import { JWT, Telephone, AdminNote, FrontEndError, PaymentValidationSet, VerificationRequests, Verification } from 'models';

export class User {
	id?: string;
	title?: string;
	firstName?: string;
	lastName?: string;
	initials?: string;
	dob: Date;
	activeListingCount?: Number;
	dealId?: string;
	avatar?: string;
	joinDate: Date;
	companyName?: string;
	billingAddressLine1?: string;
	billingAddressLine2?: string;
	billingTown?: string;
	billingCountry?: string;
	billingCountryId?: number;	
	billingCountryStateId?: number;
	billingPostcode?: string;
	whatThreeWords?: string;
	hostCode: string;
	email?: string;
	phoneNumber: number;
	telephones: Telephone[];
	claims?: UserClaim[];
	claimsToAssign?: string[];
	roles: string;
	claimsReadOnly: string;
	personalDetails?: string[];
	notes?: AdminNote[];
	accountTypeId: number;
	accountTypeName: string;
	frontEndErrors?: FrontEndError[];
	paymentValidationSet: PaymentValidationSet;
	identityBase64String: string;
	identityContentType: string;
	selfieBase64String: string;
	selfieContentType: string;
	containsIntegrations: boolean;
	latestIntegrationId: number;
	phoneNumberNew: string;
	nightlyFee: number;
	cancellationFee: number;
	protectionUpperLevel: number;
	protectionLowerLevel: number;
	currency: string;
	accountCurrencyId: number;
	accountCurrencyName: string;
	verificationRequests?: VerificationRequests[];
	verifications?: Verification[]; //TODO Tetiana - to remove after BE cleaning
	hasNewPricingStructure: boolean;
	hasProtectionPro: boolean;
	partnerCommission: number;
	clientMarkup: string;
	bookingFee: number;
	invoicingEmailAddress: string;
	techEmailAddress: string;
	flaggedNotProtected: boolean;
	monthlyGeneralDiscount?: number;
	monthlyGeneralDiscountStartDate?: Date;
	monthlyGeneralDiscountEndDate?: Date;
	monthlyVolumeDiscount?: number;
	thresholdApprovedBookingVolume?: number;
	monthlyVolumeDiscountStartDate?: Date;
	monthlyVolumeDiscountEndDate?: Date;
	priceIncrease?: number;
	priceIncreaseStartDate?: Date;
	hasApimAccount: boolean;
	hasActiveApimAccount: boolean;
	hubspotCompanyId: string;
	hyperlineId: string;
	termsAndConditionsSignedDate: Date;
	termsAndConditionsVersion: string;
	beneficiaryId: string;
	financeApprover: boolean;
	discountCode: string;
	registrationJourney: RegistrationJourneyViewModel;

	constructor(props?: { [key in keyof User]: User[key] }) {
		Object.assign(this, {
			claimsToAssign: [],
			...props,
		});
	}
}

export class UserClaim {
	issuer?: string;
	originalIssuer?: string;
	properties?: any;
	subject?: string;
	type: UserClaimType;
	// The date interceptor rewrites any ISO-8601 response string into a Date, so a
	// date-valued claim arrives here as a Date rather than the string the API sent.
	value: string | Date;
	valueType?: string;
}

// Normalised claim value for comparison. Date-valued claims yield '' so they can
// never match a named claim.
export function claimValue(claim: UserClaim): string {
	return typeof claim.value === 'string' ? claim.value.toLowerCase() : '';
}

export enum UserClaims {
	'ForcedLogin',
	'Admin',
	'Platform',
	'KnowYourGuest',
	'Guest',
	'Host',
	'NonMember',
	'VerificationPreAuth',
	'SuperAdmin',
	'FinanceApprover',
}

export type UserClaimType = keyof typeof UserClaims;
export type UserClaimTypeExtended = UserClaimType | 'Unauthenticated';

export class UserToken {
	token: string;
	claims: UserTokenClaims;
	switchedFromAdmin?: boolean;
}

export interface UserTokenClaims extends JWT.Claims {
	extended_claims: UserClaimTypeExtended | UserClaimTypeExtended[];
	nameid: string;
	role: string;
	unique_name: string;
}

export class AuthResponse {
	access_token: string;
	claims: string;
	email: string;
	expires_in: number;
	firstname: string;
	lastname: string;
	roles: string;
	token_type: 'bearer';
	username: string;
}

export class Register {
	email: string;
	title: string;
	firstName: string;
	lastName: string;
	telephones: Telephone[];
	dob: Date;
	billingAddressLine1: string;
	billingAddressLine2: string;
	billingTown: string;
	billingPostcode: string;
	billingCountryId: number;
	roleName: string;
	password: string;
	confirmPassword: string;
	isGuest: boolean;
	isHost: boolean;
	confirmedEmail: boolean;
	superhogStagingGuestTripId: number;

	constructor(props?: { [key in keyof Register]?: Register[key] }) {
		Object.assign(this, props);
		this.telephones = [];
		this.telephones.push(new Telephone());
	}
}

export class ChangePassword {
	currentPassword: string;
	newPassword: string;
	confirmPassword: string;
}

export class ResetPassword {
	id: string;
	code: string;
	confirmPassword?: string;
	newPassword?: string;

	constructor(props: { [key in keyof ResetPassword]: ResetPassword[key] }) {
		Object.assign(this, props);
	}
}

export class UserDeleteResult {
	success: boolean;
	problemMessage: string;
	errorMessage: string;
}

export class MigrateUserToNewDashResult {
	success: boolean;
	problemMessage: string;
	errorMessage: string;
}

export class EnableOrDisableResult {
	isSuccess: boolean;
	isFailure: boolean;
	error: string;
}

export class RegistrationJourneyViewModel {
	startDate: Date;
	endDate: Date;
	steps: RegistrationJourneyStepViewModel[];
	pmsAttempts: RegistrationJourneyPmsAttemptViewModel[];
}

export class RegistrationJourneyPmsAttemptViewModel {
	integrationTypeName: string;
	isSuccessful: boolean;
	date: Date;
}

export class RegistrationJourneyStepViewModel {
	startDate: Date;
	endDate: Date;
	stepType: RegistrationJourneyStepType;
	errors: RegistrationJourneyStepErrorViewModel[];
}

export class RegistrationJourneyStepErrorViewModel {
	error: string;
	date: Date;
}

export enum RegistrationJourneyStepType {
	NotStarted = 1,
	Pms = 2,
	ManualListings = 3, 
	PmsConnection = 4, 
	Programs = 5,
	Billing = 6,
	Confirmation = 7
}